import { useState } from 'react';
import { exportImagesPdf } from '../../api/images';

// Arrange & export selected images to a PDF: reorder pages (drag, sort, reverse,
// or arrows), remove pages, pick page size, then download.
export default function ExportModal({ images, onClose }) {
  const [items, setItems]       = useState(images);   // ordered image objects
  const [pageSize, setPageSize] = useState('fit');    // 'fit' | 'a4'
  const [dragIdx, setDragIdx]   = useState(null);
  const [busy, setBusy]         = useState(false);
  const [err, setErr]           = useState('');

  const move = (i, dir) =>
    setItems((prev) => {
      const j = i + dir;
      if (j < 0 || j >= prev.length) return prev;
      const next = prev.slice();
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  const remove  = (id) => setItems((prev) => prev.filter((x) => x.id !== id));
  const reverse = ()   => setItems((prev) => [...prev].reverse());

  function applySort(mode) {
    if (!mode) return;
    setItems((prev) => {
      const next = prev.slice();
      if (mode === 'name-asc')  next.sort((a, b) => a.original_name.localeCompare(b.original_name));
      if (mode === 'name-desc') next.sort((a, b) => b.original_name.localeCompare(a.original_name));
      if (mode === 'date-new')  next.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      if (mode === 'date-old')  next.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      return next;
    });
  }

  function dropOn(dropIdx) {
    setItems((prev) => {
      if (dragIdx === null || dragIdx === dropIdx) return prev;
      const next = prev.slice();
      const [moved] = next.splice(dragIdx, 1);
      next.splice(dropIdx, 0, moved);
      return next;
    });
    setDragIdx(null);
  }

  async function download() {
    setErr('');
    setBusy(true);
    try {
      const res = await exportImagesPdf(items.map((i) => i.id), pageSize);
      const url = URL.createObjectURL(res.data);
      const a   = document.createElement('a');
      a.href = url;
      a.download = 'snapvault-export.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      onClose(true);
    } catch {
      setErr('Export failed. Try fewer images.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[55] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(false); }}
    >
      <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-800 shrink-0">
          <p className="font-medium text-white text-sm">
            Export as PDF — {items.length} page{items.length !== 1 ? 's' : ''}
          </p>
          <button onClick={() => onClose(false)} className="text-gray-400 hover:text-white text-xl leading-none">×</button>
        </div>

        {/* Controls */}
        <div className="px-5 py-3 border-b border-gray-800 shrink-0 space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-gray-400 mr-1">Page size:</span>
            {[['fit', 'Fit to image'], ['a4', 'A4']].map(([v, label]) => (
              <button
                key={v}
                onClick={() => setPageSize(v)}
                className={`px-3 py-1 rounded-full text-xs transition-colors ${
                  pageSize === v ? 'bg-brand-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-gray-400 mr-1">Order:</span>
            <select
              value=""
              onChange={(e) => applySort(e.target.value)}
              className="input text-xs w-auto py-1"
            >
              <option value="">Sort by…</option>
              <option value="name-asc">Name A–Z</option>
              <option value="name-desc">Name Z–A</option>
              <option value="date-new">Newest first</option>
              <option value="date-old">Oldest first</option>
            </select>
            <button onClick={reverse} className="px-3 py-1 rounded-full text-xs bg-gray-800 text-gray-300 hover:bg-gray-700">
              ⇅ Reverse
            </button>
            <span className="text-gray-500 text-xs">or drag pages to reorder</span>
          </div>
        </div>

        {/* Reorderable page list */}
        <div className="overflow-y-auto p-3 space-y-2 flex-1">
          {items.map((img, i) => (
            <div
              key={img.id}
              draggable
              onDragStart={() => setDragIdx(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => dropOn(i)}
              onDragEnd={() => setDragIdx(null)}
              className={`flex items-center gap-3 bg-gray-800/60 rounded-lg p-2 cursor-move transition
                ${dragIdx === i ? 'opacity-40' : ''}`}
            >
              <span className="text-gray-500 select-none shrink-0" title="Drag to reorder">⠿</span>
              <span className="text-gray-500 text-xs w-5 text-center shrink-0">{i + 1}</span>
              <img
                src={`/uploads/thumbnails/${img.filename.replace(/\.[^.]+$/, '')}_thumb.webp`}
                onError={(e) => { e.currentTarget.src = `/uploads/originals/${img.filename}`; }}
                className="w-12 h-12 object-cover rounded shrink-0"
                alt=""
              />
              <span className="text-sm text-gray-200 truncate flex-1">{img.original_name}</span>
              <button onClick={() => move(i, -1)} disabled={i === 0}
                className="text-gray-400 hover:text-white disabled:opacity-30 px-1" title="Move up">↑</button>
              <button onClick={() => move(i, 1)} disabled={i === items.length - 1}
                className="text-gray-400 hover:text-white disabled:opacity-30 px-1" title="Move down">↓</button>
              <button onClick={() => remove(img.id)}
                className="text-red-400 hover:text-red-300 px-1" title="Remove from PDF">✕</button>
            </div>
          ))}
          {items.length === 0 && <p className="text-gray-500 text-sm text-center py-8">No images left.</p>}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-800 flex items-center gap-3 shrink-0">
          {err && <span className="text-xs text-red-400">{err}</span>}
          <button
            onClick={download}
            disabled={busy || items.length === 0}
            className="btn-primary text-sm ml-auto disabled:opacity-60"
          >
            {busy ? 'Generating…' : `↓ Download PDF (${items.length})`}
          </button>
        </div>
      </div>
    </div>
  );
}
